import {
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { useState } from 'react';
import { createPortal } from 'react-dom';

import { Button } from '@rockalingua/ui';
import Alert, { ALERT_TYPE } from '@/v2/components/Alert/Alert';
import StripeCardFields from '@/v2/domains/Payments/components/PaymentMethods/components/NewStripePaymentMethodForm/StripeCardFields';

import type { CardCredential } from './CardSelection';

interface NewCardFieldsProps {
  setupClientSecret: string;
  setupIntentId: string;
  confirmLabel: string;
  onConfirm: (credential: CardCredential) => Promise<void>;
  onCancel: () => void;
  footerSlot: HTMLElement | null;
  disabled?: boolean;
}

export default function NewCardFields({
  setupClientSecret,
  setupIntentId,
  confirmLabel,
  onConfirm,
  onCancel,
  footerSlot,
  disabled = false,
}: NewCardFieldsProps) {
  const stripe = useStripe();
  const elements = useElements();

  const [country, setCountry] = useState<string | undefined>(undefined);
  const [isCardReady, setIsCardReady] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canConfirm =
    !!stripe && isCardReady && !!country && !disabled && !processing;

  const confirm = async () => {
    if (!stripe || !elements || !canConfirm) return;

    setProcessing(true);
    setError(null);

    try {
      const result = await stripe.confirmCardSetup(setupClientSecret, {
        payment_method: {
          card: elements.getElement(CardNumberElement)!,
          billing_details: { address: { country } },
        },
      });

      if (result.error) {
        setError(
          result.error.message ??
            'We could not save your card. Please try again.'
        );
        return;
      }

      await onConfirm({ setupIntentId });
    } catch (e: any) {
      setError(
        e?.response?.data?.message ?? e?.message ?? 'Something went wrong'
      );
    } finally {
      setProcessing(false);
    }
  };

  return (
    <>
      <StripeCardFields
        country={country}
        onCountryChange={setCountry}
        onReadyChange={setIsCardReady}
      />
      {error && <Alert type={ALERT_TYPE.ERROR} message={error} closable />}

      {footerSlot &&
        createPortal(
          <>
            <Button onClick={onCancel} disabled={processing}>
              Cancel
            </Button>
            <Button
              type="primary"
              loading={processing}
              disabled={!canConfirm}
              onClick={confirm}
            >
              {confirmLabel}
            </Button>
          </>,
          footerSlot
        )}
    </>
  );
}
